import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  TextField,
  Box,
  Modal,
  Button,
  TableHead,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Snackbar,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styled from '@emotion/styled';
// components
import Scrollbar from '../../components/Scrollbar';
import instance from '../../api/axiosRequest';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../../components/SearchNotFound';

// ----------------------------------------------------------------------

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  marginTop: 8,
  marginLeft: 8,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

export default function Category() {
  const [openModal, setModalOpen] = useState({
    open: false,
    action: 'Add',
    id: 0,
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [reload, setReload] = useState(0);
  const [response, setResponse] = useState();
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    const getCategories = async () => {
      setLoading(true);
      setCategories([]);
      try {
        const { data } = await instance.get('/admin/listVendorCategories');
        setCategories(data.data.rows);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    getCategories();
  }, [reload]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - categories.length) : 0;

  const filteredCategories = categories.filter((e) => e.name.toLowerCase().includes(filterValue));

  return (
    <Container>
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={5}>
        <Typography variant="h4" gutterBottom>
          Categories
        </Typography>
        <Button variant="contained" onClick={() => setModalOpen({ ...openModal, open: true, action: 'Add' })}>
          Add Category
        </Button>
      </Stack>

      <Card>
        <SearchStyle
          onChange={(e) => setFilterValue(e.target.value.toLowerCase())}
          placeholder="Search name..."
          startAdornment={
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
            </InputAdornment>
          }
        />
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ID</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Created At</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              {!loading && (
                <TableBody>
                  {filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, name, createdAt } = row;

                    return (
                      <TableRow hover key={id} tabIndex={-1}>
                        <TableCell align="center">{id}</TableCell>
                        <TableCell align="center">{name}</TableCell>
                        <TableCell align="center">{new Date(createdAt).toDateString()}</TableCell>
                        <TableCell align="right">
                          <LongMenu
                            showDeleteModal={() => setModalOpen({ open: true, action: 'Delete', id, name })}
                            showUpdateModal={() => setModalOpen({ open: true, action: 'Update', id, name })}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              )}

              {filteredCategories.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      {loading ? <CircularProgress /> : <SearchNotFound searchQuery="name" />}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={categories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      <BasicModal open={openModal} setOpen={setModalOpen} setReload={setReload} setResponse={setResponse} />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={response}
        onClose={() => setResponse()}
        autoHideDuration={4000}
        message={response}
      />
    </Container>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 4,
};

function BasicModal({ open, setOpen, setReload, setResponse }) {
  const [loading, setLoading] = useState(false);
  const handleClose = () => setOpen({ open: false, action: 'Add', id: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const data = { name: e.target[0].value, vendor_category_id: open.id };
    let response;
    try {
      switch (open.action) {
        case 'Add':
          response = await instance.post('admin/addVendorCategory', data);
          break;
        case 'Update':
          response = await instance.put('admin/updateVendorCategory', data);
          break;
        case 'Delete':
          response = await instance.delete('admin/deleteVendorCategory', {
            data,
          });
          break;
        default:
          console.log('unkown option');
      }
      setLoading(false);
      handleClose();
      setResponse(response.data.message);
      setReload((state) => state + 1);
    } catch (error) {
      setResponse(error.response.data.message);
      setLoading(false);
    }
  };

  return (
    <div>
      <Modal
        open={open.open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {open.action} category
          </Typography>
          <br />
          <form onSubmit={handleSubmit}>
            <TextField
              label="Category Name"
              defaultValue={open.name || ''}
              disabled={open.action === 'delete'}
              required
              fullWidth
            />
            <Stack direction="row" justifyContent="end" mt={2}>
              <LoadingButton variant="contained" type="submit" loading={loading}>
                {open.action.toUpperCase()}
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </Modal>
    </div>
  );
}

BasicModal.propTypes = {
  open: PropTypes.shape({
    id: PropTypes.number,
    open: PropTypes.bool,
    action: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  setOpen: PropTypes.func.isRequired,
  setReload: PropTypes.func,
  setResponse: PropTypes.func,
};

function LongMenu({ showDeleteModal, showUpdateModal }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: '20ch',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            showUpdateModal();
            handleClose();
          }}
        >
          <ListItemIcon>
            <Iconify icon="eva:edit-fill" width={24} height={24} />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            showDeleteModal();
            handleClose();
          }}
        >
          <ListItemIcon>
            <Iconify icon="eva:trash-2-outline" width={24} height={24} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
}

LongMenu.propTypes = {
  showDeleteModal: PropTypes.func.isRequired,
  showUpdateModal: PropTypes.func.isRequired,
};
